import {NoopLogger, type CmLogger} from "@cm/log"
import {truncateAt} from "@cm/utils"
import {z} from "zod/v4-mini"

// need to include this to get informative error messages
// see related: https://github.com/colinhacks/zod/issues/4765
import "zod/v4"

// cycles inputs are pairs [node, socketName], where each node has a type attribute and the pairs [type, socketName] are unique
export const discriminatedCyclesUnion = <ResultType extends [{[x: string]: unknown; type: string}, string]>(
    lazyOptions: () => [z.ZodMiniObject<{type: z.ZodMiniLiteral<ResultType[0]["type"]>}>, ResultType[1]][],
    name: string,
    logger: CmLogger = NoopLogger,
): z.ZodMiniType<ResultType> =>
    z.tuple([z.looseObject({type: z.string()}), z.string()]).check((context) => {
        try {
            const applicableOption = lazyOptions().find(
                (option) => option[0].def.shape.type.def.values[0] === context.value[0].type && option[1].toLowerCase() === context.value[1].toLowerCase(),
            )
            if (applicableOption) {
                const valueAsValidOption = applicableOption[0].safeParse(context.value[0], {
                    reportInput: true,
                })
                if (valueAsValidOption.success) {
                    return
                } else {
                    logger.error(z.prettifyError(valueAsValidOption.error).split("\n").join("\n  "))
                    context.issues.push({
                        code: "custom",
                        message: `${z.prettifyError(valueAsValidOption.error).split("\n").join("\n  ")}`,
                        input: context.value,
                        path: [
                            `[${context.value[0].type}]`,
                            ...(valueAsValidOption.error.issues?.[0].path ?? []),
                            ...("path" in valueAsValidOption.error ? (valueAsValidOption.error.path as []) : []),
                        ],
                        fatal: true,
                        continue: false,
                    })
                }
            } else {
                context.issues.push({
                    code: "custom",
                    message: `Discriminated cycles union ${name} does not contain an option for [${context.value[0].type}, ${context.value[1]}]`,
                    input: truncateAt(`${context.value}`, 200),
                    path: [`[${context.value[0].type}]`],
                    fatal: true,
                    continue: false,
                })
            }
        } catch (error) {
            context.issues.push({
                code: "custom",
                message: `Error validating discriminated cycles union: ${error instanceof Error ? error.message : String(error)}`,
                input: context.value,
                fatal: true,
                continue: false,
            })
        }
    }) as unknown as z.ZodMiniType<ResultType>
